import { Injectable } from "@angular/core";
import { Amplify } from "aws-amplify";
import {
  signIn,
  getCurrentUser,
  signOut,
  fetchAuthSession,
} from "@aws-amplify/auth";
import { environment } from "../../../environments/environment";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CognitoService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private currentUserSubject = new BehaviorSubject<unknown>(null);

  isAuthenticated$: Observable<boolean> =
    this.isAuthenticatedSubject.asObservable();
  currentUser$: Observable<unknown> = this.currentUserSubject.asObservable();

  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.cognito.userPoolId,
          userPoolClientId: environment.cognito.userPoolWebClientId,
        },
      },
    });
    this.checkAuthState();
  }

  async signIn(email: string, password: string) {
    const result = await signIn({
      username: email,
      password: password,
    });

    this.isAuthenticatedSubject.next(true);
    await this.updateCurrentUser();
    return result;
  }

  async signOut(): Promise<void> {
    await signOut();
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  async getAuthToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch {
      return undefined;
    }
  }

  private async checkAuthState(): Promise<void> {
    try {
      const user = await getCurrentUser();
      this.isAuthenticatedSubject.next(true);
      this.currentUserSubject.next(user);
    } catch {
      this.isAuthenticatedSubject.next(false);
      this.currentUserSubject.next(null);
    }
  }

  private async updateCurrentUser(): Promise<void> {
    try {
      const user = await getCurrentUser();
      this.currentUserSubject.next(user);
    } catch {
      this.currentUserSubject.next(null);
    }
  }

  async getCurrentUser(): Promise<unknown> {
    try {
      const user = await getCurrentUser();
      return user;
    } catch {
      return null;
    }
  }
}
